import React, { useState, useEffect } from 'react';
import { 
  Plus, Edit2, Trash2, Save, 
  CreditCard, Briefcase, User, Shield, Bell,
  Loader, ExternalLink, CheckCircle, Clock, AlertCircle, FileSignature, Map,
  Calculator, ChevronDown, ChevronUp, X, HelpCircle, Receipt, Banknote, Key, BookOpen,
  Globe, Palette, Database 
} from 'lucide-react';
import { serviceTypesApi } from '../services/api';
import { subscriptionService } from '../services/subscriptionService';
import { formatCurrency } from '../utils/helpers';
import { useBusinessContext } from '../contexts/BusinessContext';
import Loading from '../components/Loading';
import Modal from '../components/Modal';
import Toast from '../components/Toast';
import AgreementSettingsTab from '../components/AgreementSettingsTab';
import EmailSettingsTab from '../components/EmailSettingsTab';
import businessService from '../services/businessService';
import MobileTabDropdown from '../components/MobileTabDropdown';
import TaxSettingsTab from '../components/TaxSettingsTab';
import { useSearchParams } from 'react-router-dom';

// Subscription information
import ChangePlanModal from '../components/subscription/ChangePlanModal';
import CancelSubscriptionModal from '../components/subscription/CancelSubscriptionModal';
import UpdatePaymentMethodModal from '../components/subscription/UpdatePaymentMethodModal';
import InvoiceModal from '../components/subscription/InvoiceModal';
import SubscriptionTab from '../components/subscription/SubscriptionTab';

// These are the tabs that are displayed in the settings page
import ServiceTypesTab from '../components/settings/ServiceTypesTab';
import BusinessInfoTab from '../components/settings/BusinessInfoTab';
import PaymentsTab from '../components/settings/PaymentsTab';
import SecurityTab from '../components/settings/SecurityTab';
import WebsiteGalleryTab from '../components/settings/WebsiteGalleryTab'
import RouteSettingsTab from '../components/settings/RouteSettingsTab';

// API Access
import ApiAccessTab from '../components/settings/ApiAccessTab';

// QuickBooks Integration
import QuickBooksTab from '../components/settings/QuickBooksTab';

// Online Lead Capture
import OnlineBookingTab from '../components/settings/OnlineBookingTab';

// Branding for business logo and colors
import BusinessBrandingTab from '../components/settings/BusinessBrandingTab';

// Data Export
import DataExportTab from '../components/settings/DataExportTab';

// Pricing template options
const PRICING_TEMPLATES = [
  { value: '', label: 'None (Use Default Price)', description: 'Simple flat rate pricing' },
  { value: 'Flat', label: 'Flat Rate', description: 'Fixed price per job' },
  { value: 'PerSqFt', label: 'Per Square Foot', description: 'Price × square footage' },
  { value: 'PerLinearFt', label: 'Per Linear Foot', description: 'Price × linear feet' },
  { value: 'PerItem', label: 'Per Item/Count', description: 'Price × quantity (windows, trees, etc.)' },
  { value: 'PerHour', label: 'Per Hour', description: 'Hourly rate × estimated hours' },
  { value: 'Tiered', label: 'Tiered (S/M/L)', description: 'Small, Medium, Large pricing options' },
];

// Tab configuration with groups - Reorganized to prevent overflow
// Moved Online Leads into Services group, consolidated Account tabs
const TAB_GROUPS = [
  {
    id: 'services',
    label: 'Services',
    tabs: [
      { id: 'service-types', label: 'Service Types', icon: Briefcase },
      { id: 'online-leads', label: 'Online Leads', icon: Globe, ownerOnly: true },
      { id: 'website-gallery', label: 'Website Gallery', icon: Globe, ownerOnly: true },
      { id: 'routing', label: 'Routing', icon: Map, ownerOnly: true },
    ]
  },
  {
    id: 'communications',
    label: 'Communications',
    tabs: [
      { id: 'emails', label: 'Emails', icon: Bell },
      { id: 'agreements', label: 'Agreements', icon: FileSignature },
    ]
  },
  {
    id: 'billing',
    label: 'Billing',
    tabs: [
      { id: 'tax', label: 'Tax', icon: Receipt, ownerOnly: true },
      { id: 'payments', label: 'Payments', icon: Banknote, ownerOnly: true },
      // { id: 'quickbooks', label: 'QuickBooks', icon: BookOpen, ownerOnly: true },
      { id: 'subscription', label: 'Subscription', icon: CreditCard },
    ]
  },
  {
    id: 'account',
    label: 'Account',
    tabs: [
      { id: 'business', label: 'Business', icon: User },
      { id: 'branding', label: 'Branding', icon: Palette },
      { id: 'data-export', label: 'Data', icon: Database, ownerOnly: true },
      { id: 'security', label: 'Security', icon: Shield },
      { id: 'api-access', label: 'API', icon: Key, ownerOnly: true },
    ]
  }
];

const Settings = () => {
  const { businessName, user, updateBusiness, canAccessFeature } = useBusinessContext();
  const [searchParams] = useSearchParams();
  
  // Tab state
  const [activeTab, setActiveTab] = useState(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam) {
      return tabParam;
    }
    return 'service-types';
  });

  const [toast, setToast] = useState(null);
  
  // Service types state
  const [serviceTypes, setServiceTypes] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [showCalculatorConfig, setShowCalculatorConfig] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    defaultPrice: '',
    unit: 'Job',
    category: '',
    pricingTemplate: '',
    baseRate: '',
    minimumCharge: '',
    measurementLabel: '',
    measurementHelpText: '',
    modifiers: [],
    tiers: [
      { name: 'Small', description: '', maxValue: '', price: '' },
      { name: 'Medium', description: '', maxValue: '', price: '' },
      { name: 'Large', description: '', maxValue: '', price: '' },
    ],
  });
  
  // Subscription state
  const [subscriptionInfo, setSubscriptionInfo] = useState(null);
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);

  // Subscription management modals
  const [showChangePlanModal, setShowChangePlanModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showUpdatePaymentModal, setShowUpdatePaymentModal] = useState(false);
  const [selectedPlanForChange, setSelectedPlanForChange] = useState(null);
  const [billingPeriod, setBillingPeriod] = useState('monthly');
  const [plans, setPlans] = useState([]);
  const [plansLoading, setPlansLoading] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState(null);
  const [currentBillingPeriod, setCurrentBillingPeriod] = useState(null);

  // Invoice modal state
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  
  // General loading
  const [loading, setLoading] = useState(true);

  // Check if user is Owner
  const isOwner = user?.role === 'Owner';

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      await Promise.all([
        loadServiceTypes(),
        loadSubscriptionInfo()
      ]);
    } catch (error) {
      console.error('Error loading settings data:', error);
    } finally {
      setLoading(false);
    }
  };
  
  // ============================================
  // SERVICE TYPES FUNCTIONS
  // ============================================
  const loadServiceTypes = async () => {
    try {
      const response = await serviceTypesApi.getAll();
      setServiceTypes(response.data);
    } catch (error) {
      console.error('Error loading service types:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const activeModifiers = formData.modifiers.filter(m => m.name && m.amount);
      const modifiersJson = activeModifiers.length > 0 ? JSON.stringify(activeModifiers) : null;
      
      const activeTiers = formData.pricingTemplate === 'Tiered' 
        ? formData.tiers.filter(t => t.name && t.price)
        : [];
      const tiersJson = activeTiers.length > 0 ? JSON.stringify(activeTiers) : null;

      const submitData = {
        name: formData.name,
        description: formData.description || null,
        defaultPrice: parseFloat(formData.defaultPrice) || 0,
        unit: formData.unit,
        category: formData.category || null,
        pricingTemplate: formData.pricingTemplate || null,
        baseRate: formData.baseRate ? parseFloat(formData.baseRate) : null,
        minimumCharge: formData.minimumCharge ? parseFloat(formData.minimumCharge) : null,
        measurementLabel: formData.measurementLabel || null,
        measurementHelpText: formData.measurementHelpText || null,
        modifiersJson: modifiersJson,
        tiersJson: tiersJson,
      };

      if (editingService) {
        await serviceTypesApi.update(editingService.id, { id: editingService.id, ...submitData });
      } else {
        await serviceTypesApi.create(submitData);
      }
      
      await loadServiceTypes();
      handleCloseModal();
      showToast(editingService ? 'Service type updated!' : 'Service type created!');
    } catch (error) {
      console.error('Error saving service type:', error);
      showToast('Failed to save service type', 'error');
    }
  };

  const handleEdit = (service) => {
    setEditingService(service);
    
    let modifiers = [];
    if (service.modifiersJson) {
      try {
        modifiers = JSON.parse(service.modifiersJson);
      } catch (e) {
        console.error('Error parsing modifiers:', e);
      }
    }
    
    let tiers = [
      { name: 'Small', description: '', maxValue: '', price: '' },
      { name: 'Medium', description: '', maxValue: '', price: '' },
      { name: 'Large', description: '', maxValue: '', price: '' },
    ];
    if (service.tiersJson) {
      try {
        const parsedTiers = JSON.parse(service.tiersJson);
        tiers = parsedTiers.length > 0 ? parsedTiers : tiers;
      } catch (e) {
        console.error('Error parsing tiers:', e);
      }
    }
    
    setFormData({
      name: service.name || '',
      description: service.description || '',
      defaultPrice: service.defaultPrice || '',
      unit: service.unit || 'Job',
      category: service.category || '',
      pricingTemplate: service.pricingTemplate || '',
      baseRate: service.baseRate || '',
      minimumCharge: service.minimumCharge || '',
      measurementLabel: service.measurementLabel || '',
      measurementHelpText: service.measurementHelpText || '',
      modifiers: modifiers,
      tiers: tiers,
    });
    
    setShowCalculatorConfig(!!service.pricingTemplate);
    setShowModal(true);
  };

  const handleDelete = async (serviceId) => {
    if (!window.confirm('Are you sure you want to delete this service type?')) {
      return;
    }

    try {
      await serviceTypesApi.delete(serviceId);
      await loadServiceTypes();
      showToast('Service type deleted');
    } catch (error) {
      console.error('Error deleting service type:', error);
      showToast('Failed to delete service type', 'error');
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingService(null);
    setShowCalculatorConfig(false);
    setFormData({
      name: '',
      description: '',
      defaultPrice: '',
      unit: 'Job',
      category: '',
      pricingTemplate: '',
      baseRate: '',
      minimumCharge: '',
      measurementLabel: '',
      measurementHelpText: '',
      modifiers: [],
      tiers: [
        { name: 'Small', description: '', maxValue: '', price: '' },
        { name: 'Medium', description: '', maxValue: '', price: '' },
        { name: 'Large', description: '', maxValue: '', price: '' },
      ],
    });
  };

  const handlePricingTemplateChange = (template) => {
    let measurementLabel = '';
    let measurementHelpText = '';
    let unit = formData.unit;
    
    switch (template) {
      case 'PerSqFt':
        measurementLabel = 'Square Footage';
        measurementHelpText = 'Find this on property tax records, Zillow, or MLS listing';
        unit = 'SqFt';
        break;
      case 'PerLinearFt':
        measurementLabel = 'Linear Feet';
        measurementHelpText = 'Measure the total length';
        unit = 'Linear Ft';
        break;
      case 'PerItem':
        measurementLabel = 'Number of Items';
        measurementHelpText = 'Count the total items';
        unit = 'Item';
        break;
      case 'PerHour':
        measurementLabel = 'Estimated Hours';
        measurementHelpText = 'How long will this job take?';
        unit = 'Hour';
        break;
      case 'Tiered':
        measurementLabel = 'Size';
        measurementHelpText = 'Select the appropriate size tier';
        unit = 'Job';
        break;
      default:
        unit = 'Job';
    }
    
    setFormData({
      ...formData,
      pricingTemplate: template,
      measurementLabel: formData.measurementLabel || measurementLabel,
      measurementHelpText: formData.measurementHelpText || measurementHelpText,
      unit: unit,
    });
  };

  const addModifier = () => {
    setFormData({
      ...formData,
      modifiers: [...formData.modifiers, { name: '', type: 'flat', amount: '', enabled: true }]
    });
  };

  const updateModifier = (index, field, value) => {
    const newModifiers = [...formData.modifiers];
    newModifiers[index] = { ...newModifiers[index], [field]: value };
    setFormData({ ...formData, modifiers: newModifiers });
  };

  const removeModifier = (index) => {
    setFormData({
      ...formData,
      modifiers: formData.modifiers.filter((_, i) => i !== index)
    });
  };

  const updateTier = (index, field, value) => {
    const newTiers = [...formData.tiers];
    newTiers[index] = { ...newTiers[index], [field]: value };
    setFormData({ ...formData, tiers: newTiers });
  };

  const addTier = () => {
    setFormData({
      ...formData,
      tiers: [...formData.tiers, { name: '', description: '', maxValue: '', price: '' }]
    });
  };

  const removeTier = (index) => {
    if (formData.tiers.length > 1) {
      setFormData({
        ...formData,
        tiers: formData.tiers.filter((_, i) => i !== index)
      });
    }
  };

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
  };

  // ============================================
  // SUBSCRIPTION FUNCTIONS
  // ============================================
  const loadSubscriptionInfo = async () => {
    try {
      setSubscriptionLoading(true);
      const info = await subscriptionService.getStatus();
      setSubscriptionInfo(info);
      
      if (info.billingPeriod) {
        setCurrentBillingPeriod(info.billingPeriod.toLowerCase());
      }
      
      if (info.subscriptionStatus === 'Active') {
        await Promise.all([
          loadPlans(),
          loadPaymentMethod()
        ]);
      }
    } catch (error) {
      console.error('Error loading subscription:', error);
    } finally {
      setSubscriptionLoading(false);
    }
  };

  const handleManageSubscription = async () => {
    try {
      setPortalLoading(true);
      const returnUrl = window.location.href;
      const portal = await subscriptionService.createPortalSession(returnUrl);
      subscriptionService.redirectToPortal(portal.url);
    } catch (error) {
      console.error('Error opening customer portal:', error);
      showToast('Failed to open subscription management. Please try again.', 'error');
      setPortalLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      Active: { color: 'bg-green-100 text-green-800', label: 'Active' },
      Trial: { color: 'bg-blue-100 text-blue-800', label: 'Trial' },
      PastDue: { color: 'bg-yellow-100 text-yellow-800', label: 'Past Due' },
      Cancelled: { color: 'bg-red-100 text-red-800', label: 'Canceled' },
      Incomplete: { color: 'bg-gray-100 text-gray-800', label: 'Incomplete' },
      Expired: { color: 'bg-red-100 text-red-800', label: 'Expired' },
    };
    
    const badge = badges[status] || badges.Incomplete;
    return (
      <span className={`px-3 py-1 rounded-full text-sm font-medium ${badge.color}`}>
        {badge.label}
      </span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const loadPlans = async () => {
    try {
      setPlansLoading(true);
      const plansData = await subscriptionService.getPlans();
      setPlans(plansData);
    } catch (error) {
      console.error('Error loading plans:', error);
    } finally {
      setPlansLoading(false);
    }
  };

  const loadPaymentMethod = async () => {
    try {
      const pm = await subscriptionService.getPaymentMethod();
      setPaymentMethod(pm);
    } catch (error) {
      console.error('Error loading payment method:', error);
    }
  };

  const handlePlanChangeSuccess = async (newPlan) => {
    await loadSubscriptionInfo();
    showToast(`Successfully changed to ${newPlan} plan!`);
  };

  const handleCancelSuccess = async (immediate) => {
    await loadSubscriptionInfo();
    if (immediate) {
      showToast('Subscription cancelled immediately');
      setTimeout(() => {
        window.location.href = '/subscription-cancelled';
      }, 2000);
    } else {
      showToast('Subscription will cancel at the end of your billing period');
    }
  };

  const handlePaymentMethodUpdateSuccess = async () => {
    await loadPaymentMethod();
    showToast('Payment method updated successfully!');
  };

  const handleViewInvoice = (invoice) => {
    setSelectedInvoice(invoice);
    setShowInvoiceModal(true);
  };

  const getPricingBadge = (service) => {
    if (!service.pricingTemplate) return null;
    
    const template = PRICING_TEMPLATES.find(t => t.value === service.pricingTemplate);
    if (!template) return null;
    
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded-full">
        <Calculator size={10} />
        {template.label}
      </span>
    );
  };

  // Get visible tabs based on user role
  const getVisibleTabs = () => {
    return TAB_GROUPS.map(group => ({
      ...group,
      tabs: group.tabs.filter(tab => {
        if (tab.ownerOnly && !isOwner) return false;
        if (tab.id === 'routing' && !canAccessFeature('route-optimization')) return false;
        return true;
      })
    })).filter(group => group.tabs.length > 0);
  };

  if (loading) return <Loading fullScreen />;

  const visibleGroups = getVisibleTabs();
  
  return (
    <div className="p-4 lg:p-8 pb-20 lg:pb-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Settings</h1>
        <p className="text-gray-600 mt-1">Manage your business settings and preferences</p>
      </div>

      {/* Grouped Tabs Navigation */}
      <div className="mb-6">
        {/* Desktop: Horizontal grouped tabs with scroll */}
        <div className="hidden md:block overflow-x-auto">
          <div className="flex items-end gap-6 border-b border-gray-200 min-w-max pb-px">
            {visibleGroups.map((group) => (
              <div key={group.id} className="flex flex-col">
                <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1 px-1">
                  {group.label}
                </span>
                <div className="flex items-center">
                  {group.tabs.map((tab) => {
                    const Icon = tab.icon;
                    return (
                      <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className={`flex items-center px-2.5 py-2 font-medium text-sm whitespace-nowrap transition-colors border-b-2 -mb-px ${
                          activeTab === tab.id
                            ? 'border-primary-600 text-primary-600'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        }`}
                      >
                        <Icon size={15} className="mr-1" />
                        {tab.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Mobile: Dropdown select */}
        <div className="md:hidden">
          <MobileTabDropdown 
            groups={visibleGroups} 
            activeTab={activeTab} 
            onTabChange={setActiveTab} 
          />
        </div>
      </div>

      {/* Tab Content */}
      <div className="space-y-6">
        
        {/* SERVICE TYPES TAB */}
        {activeTab === 'service-types' && <ServiceTypesTab />}

        {/* EMAIL NOTIFICATIONS TAB */}
        {activeTab === 'emails' && (
          <EmailSettingsTab showToast={showToast} />
        )}

        {/* AGREEMENTS TAB */}
        {activeTab === 'agreements' && (
          <AgreementSettingsTab showToast={showToast} />
        )}

        {/* ONLINE LEADS TAB */}
        {activeTab === 'online-leads' && isOwner && (
          <OnlineBookingTab />
        )}

        {/* WEBSITE GALLERY TAB */}
        {activeTab === 'website-gallery' && isOwner && (
          <WebsiteGalleryTab showToast={showToast} />
        )}
		
        {/* ROUTE OPTIMIZATION TAB */}
        {activeTab === 'routing' && isOwner && (
          <RouteSettingsTab showToast={showToast} />
        )}

        {/* SUBSCRIPTION TAB */}
        {activeTab === 'subscription' && (
          <SubscriptionTab showToast={showToast} />
        )}

        {/* BUSINESS INFO TAB */}
        {activeTab === 'business' && (
          <BusinessInfoTab user={user} businessName={businessName} />
        )}

        {/* BRANDING TAB */}
        {activeTab === 'branding' && <BusinessBrandingTab />}

        {/* DATA EXPORT TAB */}
        {activeTab === 'data-export' && isOwner && (
          <DataExportTab showToast={showToast} />
        )}

        {/* STRIPE CONNECT PAYMENTS */}
        {activeTab === 'payments' && <PaymentsTab />}

        {/* QUICKBOOKS TAB */}
        {/* {activeTab === 'quickbooks' && isOwner && (
          <QuickBooksTab showToast={showToast} />
        )} */}
        
        {/* TAX SETTINGS TAB */}
        {activeTab === 'tax' && isOwner && (
          <TaxSettingsTab showToast={showToast} />
        )}

        {/* SECURITY TAB */}
        {activeTab === 'security' && (
          <SecurityTab showToast={showToast} />
        )}

        {/* API ACCESS TAB */}
        {activeTab === 'api-access' && (
          <ApiAccessTab showToast={showToast} />
        )}
      </div>

      {/* Add/Edit Service Type Modal */}
      <Modal
        isOpen={showModal}
        onClose={handleCloseModal}
        title={editingService ? 'Edit Service Type' : 'Add Service Type'}
        size="lg"
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="label">Service Name *</label>
            <input
              type="text"
              required
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="input"
              placeholder="e.g., House Wash, Driveway Cleaning"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label">Category</label>
              <input
                type="text"
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                className="input"
                placeholder="e.g., Exterior, Seasonal"
              />
            </div>
            <div>
              <label className="label">Default Price *</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">$</span>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  required
                  value={formData.defaultPrice}
                  onChange={(e) => setFormData({ ...formData, defaultPrice: e.target.value })}
                  className="input pl-7"
                  placeholder="0.00"
                />
              </div>
            </div>
          </div>

          <div>
            <label className="label">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="input"
              rows="2"
              placeholder="Brief description of the service"
            />
          </div>

          <div className="border-t border-gray-200 pt-4">
            <button
              type="button"
              onClick={() => setShowCalculatorConfig(!showCalculatorConfig)}
              className="flex items-center justify-between w-full p-3 bg-purple-50 border border-purple-200 rounded-lg hover:bg-purple-100 transition-colors"
            >
              <div className="flex items-center gap-3">
                <Calculator className="text-purple-600" size={20} />
                <div className="text-left">
                  <p className="font-medium text-purple-900">Quote Calculator</p>
                  <p className="text-xs text-purple-700">Configure automatic price calculations</p>
                </div>
              </div>
              {showCalculatorConfig ? <ChevronUp size={20} className="text-purple-600" /> : <ChevronDown size={20} className="text-purple-600" />}
            </button>
          </div>

          {showCalculatorConfig && (
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 space-y-4">
              <div>
                <label className="label">Pricing Method</label>
                <select
                  value={formData.pricingTemplate}
                  onChange={(e) => handlePricingTemplateChange(e.target.value)}
                  className="input"
                >
                  {PRICING_TEMPLATES.map((template) => (
                    <option key={template.value} value={template.value}>
                      {template.label} - {template.description}
                    </option>
                  ))}
                </select>
              </div>

              {['PerSqFt', 'PerLinearFt', 'PerItem', 'PerHour'].includes(formData.pricingTemplate) && (
                <>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="label">Rate Per {formData.unit || 'Unit'} *</label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">$</span>
                        <input
                          type="number"
                          step="0.0001"
                          min="0"
                          value={formData.baseRate}
                          onChange={(e) => setFormData({ ...formData, baseRate: e.target.value })}
                          className="input pl-7"
                          placeholder="0.15"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="label">Minimum Charge</label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">$</span>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={formData.minimumCharge}
                          onChange={(e) => setFormData({ ...formData, minimumCharge: e.target.value })}
                          className="input pl-7"
                          placeholder="150"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="label">Measurement Label</label>
                      <input
                        type="text"
                        value={formData.measurementLabel}
                        onChange={(e) => setFormData({ ...formData, measurementLabel: e.target.value })}
                        className="input"
                        placeholder="e.g., Square Footage"
                      />
                    </div>
                    <div>
                      <label className="label">Help Text</label>
                      <input
                        type="text"
                        value={formData.measurementHelpText}
                        onChange={(e) => setFormData({ ...formData, measurementHelpText: e.target.value })}
                        className="input"
                        placeholder="e.g., Find on tax records"
                      />
                    </div>
                  </div>
                </>
              )}

              {formData.pricingTemplate === 'Tiered' && (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="label mb-0">Pricing Tiers</label>
                    <button
                      type="button"
                      onClick={addTier}
                      className="text-xs text-purple-600 hover:text-purple-700 font-medium"
                    >
                      + Add Tier
                    </button>
                  </div>
                  <div className="space-y-2">
                    {formData.tiers.map((tier, index) => (
                      <div key={index} className="flex items-center gap-2 bg-white p-2 rounded-lg border border-purple-100">
                        <input
                          type="text"
                          value={tier.name}
                          onChange={(e) => updateTier(index, 'name', e.target.value)}
                          className="input py-1.5 text-sm flex-1"
                          placeholder="Tier name"
                        />
                        <input
                          type="text"
                          value={tier.description}
                          onChange={(e) => updateTier(index, 'description', e.target.value)}
                          className="input py-1.5 text-sm flex-1"
                          placeholder="Description"
                        />
                        <div className="relative w-24">
                          <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 text-xs">$</span>
                          <input
                            type="number"
                            step="0.01"
                            value={tier.price}
                            onChange={(e) => updateTier(index, 'price', e.target.value)}
                            className="input py-1.5 text-sm pl-5 w-full"
                            placeholder="Price"
                          />
                        </div>
                        {formData.tiers.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeTier(index)}
                            className="p-1 text-red-400 hover:text-red-600"
                          >
                            <X size={16} />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {formData.pricingTemplate && formData.pricingTemplate !== 'Tiered' && (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="label mb-0">Price Modifiers (Optional)</label>
                    <button
                      type="button"
                      onClick={addModifier}
                      className="text-xs text-purple-600 hover:text-purple-700 font-medium"
                    >
                      + Add Modifier
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 mb-2">
                    Add surcharges like "2-Story (+$100)" or "Heavy Staining (+25%)"
                  </p>
                  
                  {formData.modifiers.length === 0 ? (
                    <p className="text-sm text-gray-400 italic text-center py-2">
                      No modifiers configured
                    </p>
                  ) : (
                    <div className="space-y-2">
                      {formData.modifiers.map((modifier, index) => (
                        <div key={index} className="flex items-center gap-2 bg-white p-2 rounded-lg border border-purple-100">
                          <input
                            type="text"
                            value={modifier.name}
                            onChange={(e) => updateModifier(index, 'name', e.target.value)}
                            className="input py-1.5 text-sm flex-1"
                            placeholder="Name (e.g., 2-Story)"
                          />
                          <select
                            value={modifier.type}
                            onChange={(e) => updateModifier(index, 'type', e.target.value)}
                            className="input py-1.5 text-sm w-24"
                          >
                            <option value="flat">+ $</option>
                            <option value="percent">+ %</option>
                          </select>
                          <input
                            type="number"
                            step={modifier.type === 'percent' ? '1' : '0.01'}
                            value={modifier.amount}
                            onChange={(e) => updateModifier(index, 'amount', e.target.value)}
                            className="input py-1.5 text-sm w-20"
                            placeholder={modifier.type === 'percent' ? '25' : '100'}
                          />
                          <label className="flex items-center gap-1 text-xs">
                            <input
                              type="checkbox"
                              checked={modifier.enabled}
                              onChange={(e) => updateModifier(index, 'enabled', e.target.checked)}
                              className="rounded border-gray-300 text-purple-600"
                            />
                            On
                          </label>
                          <button
                            type="button"
                            onClick={() => removeModifier(index)}
                            className="p-1 text-red-400 hover:text-red-600"
                          >
                            <X size={16} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {formData.pricingTemplate && formData.baseRate && (
                <div className="bg-white border border-purple-200 rounded-lg p-3">
                  <p className="text-xs font-medium text-purple-900 mb-2">Preview:</p>
                  <p className="text-sm text-gray-700">
                    {formData.pricingTemplate === 'PerSqFt' && `1,800 sq ft × $${formData.baseRate} = $${(1800 * parseFloat(formData.baseRate || 0)).toFixed(2)}`}
                    {formData.pricingTemplate === 'PerLinearFt' && `200 linear ft × $${formData.baseRate} = $${(200 * parseFloat(formData.baseRate || 0)).toFixed(2)}`}
                    {formData.pricingTemplate === 'PerItem' && `24 items × $${formData.baseRate} = $${(24 * parseFloat(formData.baseRate || 0)).toFixed(2)}`}
                    {formData.pricingTemplate === 'PerHour' && `3 hours × $${formData.baseRate} = $${(3 * parseFloat(formData.baseRate || 0)).toFixed(2)}`}
                  </p>
                  {formData.minimumCharge && (
                    <p className="text-xs text-gray-500 mt-1">
                      Minimum: ${parseFloat(formData.minimumCharge).toFixed(2)}
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          <div className="flex flex-col-reverse sm:flex-row gap-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={handleCloseModal}
              className="btn btn-secondary flex-1 justify-center"
            >
              Cancel
            </button>
            <button 
              type="submit" 
              className="btn btn-primary flex-1 justify-center items-center gap-2"
            >
              <Save size={18} />
              {editingService ? 'Update Service' : 'Add Service'}
            </button>
          </div>
        </form>
      </Modal>
	  
      {/* Subscription Management Modals */}
      <ChangePlanModal
        isOpen={showChangePlanModal}
        onClose={() => {
          setShowChangePlanModal(false);
          setSelectedPlanForChange(null);
        }}
        currentPlan={subscriptionInfo?.subscriptionPlan}
        currentBillingPeriod={currentBillingPeriod}
        targetPlan={selectedPlanForChange}
        billingPeriod={billingPeriod}
        onSuccess={handlePlanChangeSuccess}
      />

      <CancelSubscriptionModal
        isOpen={showCancelModal}
        onClose={() => setShowCancelModal(false)}
        currentPlan={subscriptionInfo?.subscriptionPlan}
        onSuccess={handleCancelSuccess}
      />

      <UpdatePaymentMethodModal
        isOpen={showUpdatePaymentModal}
        onClose={() => setShowUpdatePaymentModal(false)}
        onSuccess={handlePaymentMethodUpdateSuccess}
        currentCard={paymentMethod}
      />

      <InvoiceModal
        isOpen={showInvoiceModal}
        onClose={() => {
          setShowInvoiceModal(false);
          setSelectedInvoice(null);
        }}
        invoice={selectedInvoice}
      />
      
      {/* Toast Notifications */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
};

export default Settings;